import useAuth from "../../hooks/useAuth";
import { useState, useEffect } from "react";
import { getInvoiceById, recordPayment, deleteInvoice } from '../../api/invoices';
import { getDeliveryNoteById } from '../../api/orders';
import { Badge, Alert, Card, Row, Col, Button, Table } from "react-bootstrap";
import Loading from "../common/Loading";
import { formatDate, formatCurrency } from "../../utils/formatters";
import { ArrowLeft, Download, CreditCard, Edit, Trash2, Clock, Eye, Printer } from "lucide-react";
import { createRoot } from 'react-dom/client';
import GenererPdfFacture from "./genererPdfFacture";

const InvoiceDetail = ({ invoiceId, editMode, onCancel, onSuccess, onEdit, companyInfo, show }) => {
    const { isAdmin, isManager, isSalesRep, isDistributor } = useAuth();

    const [invoice, setInvoice] = useState(null);
    const [deliveryNote, setDeliveryNote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Nouveaux états pour les calculs financiers
    const [subtotal, setSubtotal] = useState(0);
    const [discountedTotal, setDiscountedTotal] = useState(0);
    const [taxAmount, setTaxAmount] = useState(0);
    const [totalTTC, setTotalTTC] = useState(0);

    useEffect(() => {
        const fetchInvoiceDetails = async () => {
            setLoading(true);
            try {
                const data = await getInvoiceById(invoiceId);
                setInvoice(data);

                // Charger le bon de livraison facturé (pour ses lignes)
                if (data.deliveryNoteId) {
                    try {
                        const bl = await getDeliveryNoteById(data.deliveryNoteId);
                        setDeliveryNote(bl);
                    } catch (e) {
                        console.error("BL introuvable pour la facture", e);
                    }
                }

                // Calcul des montants financiers
                calculateFinancials(data);
            } catch (err) {
                setError("Impossible de charger les détails de la facture");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        if (invoiceId) {
            fetchInvoiceDetails();
        }
    }, [invoiceId]);

    // Fonction pour calculer tous les montants financiers
    const calculateFinancials = (invoiceData) => {
        if (!invoiceData) return;

        // Montant HT (déjà présent dans totalAmount)
        const subtotalAmount = invoiceData.totalAmount;

        // Montant après remise globale
        const discountAmount = invoiceData.discountAmount || 0;
        const discountedSubtotal = subtotalAmount - discountAmount;

        // TVA appliquée uniquement si la commande y est assujettie
        const taxRate = invoiceData.order?.vatApplicable ? 18 : 0;
        const taxAmountValue = (discountedSubtotal * taxRate) / 100;

        // Total TTC
        const totalWithTax = discountedSubtotal + taxAmountValue;

        // Mise à jour des états
        setSubtotal(subtotalAmount);
        setDiscountedTotal(discountedSubtotal);
        setTaxAmount(taxAmountValue);
        setTotalTTC(totalWithTax);
    };

    const handlePaymentRecording = async () => {
        // Vérifier si l'utilisateur a les droits pour enregistrer des paiements
        if (isDistributor) {
            setError('Vous n\'avez pas les droits pour enregistrer des paiements. Contactez votre responsable commercial.');
            setTimeout(() => setError(''), 5000);
            return;
        }

        const confirmed = window.confirm('Êtes-vous sûr de vouloir enregistrer ce paiement ?');

        if (!confirmed) {
            return; // Annuler l'opération si l'utilisateur clique sur "Annuler"
        }

        try {
            const updatedInvoice = await recordPayment(invoiceId);
            setInvoice(updatedInvoice);
            calculateFinancials(updatedInvoice);
            setSuccess("Paiement enregistré avec succès");

            // Effacer le message après 3 secondes
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message || "Erreur lors de l'enregistrement du paiement");

            // Effacer le message d'erreur après 3 secondes
            setTimeout(() => setError(''), 3000);
        }
    };



    const handleDeleteInvoice = async () => {
        // Seuls les admins peuvent supprimer des factures
        if (isDistributor) {
            setError('Vous n\'avez pas les droits pour supprimer des factures.');
            setTimeout(() => setError(''), 5000);
            return;
        }

        if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette facture ?")) {
            return;
        }

        try {
            await deleteInvoice(invoiceId);
            // navigate('/invoices', { state: { message: "Facture supprimée avec succès" } });
        } catch (err) {
            setError(err.message || "Impossible de supprimer cette facture");

            // Effacer le message d'erreur après 3 secondes
            setTimeout(() => setError(''), 3000);
        }
    };

    const handlePrintInvoice = () => {
        if (!invoice) return;

        // Créer un élément pour le rendu du PDF d'impression
        const printElement = document.createElement('div');
        document.body.appendChild(printElement);

        const root = createRoot(printElement);

        // Passer les montants calculés au composant de génération PDF
        root.render(
            <GenererPdfFacture
                invoice={invoice}
                company={companyInfo}
                deliveryNote={deliveryNote}
                financials={{
                    subtotal,
                    discountAmount: invoice.discountAmount || 0,
                    discountedTotal,
                    taxRate: invoice.client?.taxRate || 18,
                    taxAmount,
                    totalTTC
                }}
                onDownload={(blob) => {
                    const url = URL.createObjectURL(blob);

                    // Ouvrir dans une nouvelle fenêtre pour impression
                    const printWindow = window.open(url, '_blank');
                    if (printWindow) {
                        printWindow.onload = () => {
                            printWindow.print();
                        };
                    }

                    // Nettoyage
                    setTimeout(() => {
                        URL.revokeObjectURL(url);
                        document.body.removeChild(printElement);
                    }, 100);
                }}
            />
        );
    };

    const handleDownloadInvoice = () => {
        if (!invoice) return;

        // Créer un élément pour le rendu du PDF
        const pdfElement = document.createElement('div');
        document.body.appendChild(pdfElement);
        const root = createRoot(pdfElement);

        root.render(
            <GenererPdfFacture
                invoice={invoice}
                company={companyInfo}
                deliveryNote={deliveryNote}
                financials={{
                    subtotal,
                    discountAmount: invoice.discountAmount || 0,
                    discountedTotal,
                    taxRate: invoice.client?.taxRate || 18,
                    taxAmount,
                    totalTTC
                }}
                onDownload={(blob) => {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `facture_${invoice.invoiceNumber}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    document.body.removeChild(pdfElement);
                }}
            />
        );
    };

    const handleEditInvoice = () => {
        // Seuls les admins et sales peuvent éditer
        if (isDistributor) {
            setError('Vous n\'avez pas les droits pour modifier des factures.');
            setTimeout(() => setError(''), 5000);
            return;
        }
        onEdit(); // Appelle le parent pour changer l'état
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Badge bg="warning">En attente</Badge>;
            case 'CONFIRMED':
                return <Badge bg="success">Confirmée</Badge>;
            case 'DELIVERED':
                return <Badge bg="success">Livrée</Badge>;
            case 'REJECTED':
                return <Badge bg="success">Rejetée</Badge>;
            case 'PAID':
                return <Badge bg="success">Payée</Badge>;
            case 'OVERDUE':
                return <Badge bg="danger">En retard</Badge>;
            case 'CANCELLED':
                return <Badge bg="secondary">Annulée</Badge>;
            default:
                return <Badge bg="info">{status}</Badge>;
        }
    };

    if (loading) {
        return <Loading />;
    }

    if (!invoice) {
        return (
            <Alert variant="danger">
                Facture introuvable. Veuillez vérifier l'identifiant ou contacter l'administrateur.
            </Alert>
        );
    }

    // Calcul du statut de paiement et jours restants
    const today = new Date();
    const dueDate = new Date(invoice.dueDate);
    const daysRemaining = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    let paymentStatus;
    if (invoice.status === 'PAID') {
        paymentStatus = 'Payée le ' + formatDate(invoice.paidDate);
    } else if (invoice.status === 'CANCELLED') {
        paymentStatus = 'Annulée';
    } else if (daysRemaining < 0) {
        paymentStatus = `En retard de ${Math.abs(daysRemaining)} jours`;
    } else if (daysRemaining === 0) {
        paymentStatus = 'Due aujourd\'hui';
    } else {
        paymentStatus = `Due dans ${daysRemaining} jours`;
    }

    return (
        <div>
            <Card className="shadow-sm mb-4">
                <Card.Header>
                    <Row className="align-items-center g-2">
                        <Col xs={12} sm>
                            <h5 className="mb-0">
                                {isDistributor ? 'Détails de ma Facture' : 'Détails de la Facture'}
                            </h5>
                        </Col>
                        <Col xs={12} sm="auto">
                            <div className="d-flex flex-wrap gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => onCancel()}
                                >
                                    <ArrowLeft size={16} className="me-1" />
                                    Retour
                                </Button>

                                {/* Bouton télécharger PDF - accessible à tous */}
                                {/* <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleDownloadInvoice}
                                    title="Télécharger la facture en PDF"
                                >
                                    <Download size={16} className="me-1" />
                                    Télécharger
                                </Button> */}

                                {/* Bouton imprimer - accessible à tous */}
                                {/* <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={handlePrintInvoice}
                                    title="Imprimer la facture"
                                >
                                    <Printer size={16} className="me-1" />
                                    Imprimer
                                </Button> */}

                                {/* Boutons administratifs - seulement pour Admin/Sales */}
                                {!isDistributor && (
                                    <>
                                        {invoice.status === 'PENDING' && (
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                onClick={handlePaymentRecording}
                                            >
                                                <CreditCard size={16} className="me-1" />
                                                Enregistrer paiement
                                            </Button>
                                        )}

                                        {/* {isAdmin && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={handleDeleteInvoice}
                                            >
                                                <Trash2 size={16} className="me-1" />
                                                Supprimer
                                            </Button>
                                        )} */}
                                    </>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}

                    <Row className="mb-4 g-3">
                        <Col md={8}>
                            <Card className="h-100">
                                <Card.Body>
                                    <Row className="mb-3">
                                        <Col>
                                            <h6 className="text-muted mb-1">Numéro de facture</h6>
                                            <h5>{invoice.invoiceNumber}</h5>
                                        </Col>
                                        <Col className="text-end">
                                            <h6 className="text-muted mb-1">Statut</h6>
                                            <div>{getStatusBadge(invoice.status)}</div>
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <h6 className="text-muted mb-1">
                                                {isDistributor ? 'Mes informations' : 'Client'}
                                            </h6>
                                            <p className="mb-0">{invoice.client.name}</p>
                                            <p className="mb-0 text-break">{invoice.client.email}</p>
                                            <p className="mb-0">{invoice.client.phone}</p>
                                        </Col>
                                        <Col md={6} className="mt-3 mt-md-0">
                                            <h6 className="text-muted mb-1">Adresse de facturation</h6>
                                            <p className="mb-0">{invoice.client.address}</p>
                                            <p className="mb-0">{invoice.client.postalCode} {invoice.client.city}</p>
                                            <p className="mb-0">{invoice.client.country}</p>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col xs={12} md={4}>
                                            <h6 className="text-muted mb-1">Date d'émission</h6>
                                            <p>{formatDate(invoice.issueDate)}</p>
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <h6 className="text-muted mb-1">Date d'échéance</h6>
                                            <p>{formatDate(invoice.dueDate)}</p>
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <h6 className="text-muted mb-1">Conditions de paiement</h6>
                                            <p>{invoice.paymentTerms} jours</p>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className={`h-100 ${invoice.status === 'OVERDUE' ? 'border-danger' : ''}`}>
                                <Card.Body className="d-flex flex-column justify-content-between">
                                    <div>
                                        <h6 className="text-muted mb-1">Montant total TTC</h6>
                                        <h3 className="mb-4">{formatCurrency(totalTTC)}</h3>
                                    </div>

                                    <div>
                                        <h6 className="text-muted mb-1">Statut de paiement</h6>
                                        <div className="d-flex align-items-center">
                                            <Clock size={16} className="me-2" />
                                            <span>{paymentStatus}</span>
                                        </div>

                                        {invoice.status === 'PAID' && (
                                            <div className="mt-3 alert alert-success py-2">
                                                <small className="mb-0">
                                                    <strong>Payée le:</strong> {formatDate(invoice.paidDate)}
                                                </small>
                                            </div>
                                        )}

                                        {invoice.status === 'PENDING' && daysRemaining <= 7 && daysRemaining > 0 && (
                                            <div className="mt-3 alert alert-warning py-2">
                                                <small className="mb-0">
                                                    Échéance proche ! La facture doit être payée dans {daysRemaining} jour{daysRemaining > 1 ? 's' : ''}.
                                                </small>
                                            </div>
                                        )}

                                        {invoice.status === 'OVERDUE' && (
                                            <div className="mt-3 alert alert-danger py-2">
                                                <small className="mb-0">
                                                    Cette facture est en retard de {Math.abs(daysRemaining)} jour{Math.abs(daysRemaining) > 1 ? 's' : ''}.
                                                </small>
                                            </div>
                                        )}

                                        {/* Message informatif pour les distributeurs */}
                                        {isDistributor && invoice.status === 'PENDING' && (
                                            <div className="mt-3 alert alert-info py-2">
                                                <small className="mb-0">
                                                    <strong>Note:</strong> Pour effectuer un paiement, contactez votre responsable commercial.
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Card className="mb-4">
                        <Card.Header>
                            <h6 className="mb-0">Commande &amp; bon de livraison</h6>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3 g-3">
                                <Col xs={12} md={3}>
                                    <h6 className="text-muted mb-1">Numéro de commande</h6>
                                    <p className="mb-0">{invoice.order.orderNumber}</p>
                                </Col>
                                <Col xs={12} md={3}>
                                    <h6 className="text-muted mb-1">Bon de livraison</h6>
                                    <p className="mb-0">{invoice.deliveryNoteReference || '—'}</p>
                                </Col>
                                <Col xs={12} md={3}>
                                    <h6 className="text-muted mb-1">Date de commande</h6>
                                    <p className="mb-0">{formatDate(invoice.order.orderDate)}</p>
                                </Col>
                                <Col xs={12} md={3}>
                                    <h6 className="text-muted mb-1">Statut de la commande</h6>
                                    <p className="mb-0">{getStatusBadge(invoice.order.status)}</p>
                                </Col>
                            </Row>

                            {deliveryNote && Array.isArray(deliveryNote.items) && deliveryNote.items.length > 0 && (
                                <Table responsive bordered size="sm" className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Produit</th>
                                            <th className="text-center">Qté livrée</th>
                                            <th className="text-end">Prix unitaire</th>
                                            <th className="text-end">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {deliveryNote.items.map((it) => (
                                            <tr key={it.id}>
                                                <td>{it.productName+' ('+it.productCode+')'}</td>
                                                <td className="text-center">{it.deliveredQuantity}</td>
                                                <td className="text-end">{formatCurrency(it.unitPrice)}</td>
                                                <td className="text-end">
                                                    {formatCurrency((Number(it.deliveredQuantity) || 0) * (Number(it.unitPrice) || 0))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="3" className="text-end fw-bold">Sous-total HT</td>
                                            <td className="text-end fw-bold">{formatCurrency(subtotal)}</td>
                                        </tr>
                                        {invoice.discountAmount > 0 && (
                                            <tr>
                                                <td colSpan="3" className="text-end">Remise</td>
                                                <td className="text-end">- {formatCurrency(invoice.discountAmount)}</td>
                                            </tr>
                                        )}
                                        {invoice.order?.vatApplicable && (
                                            <tr>
                                                <td colSpan="3" className="text-end">TVA (18%)</td>
                                                <td className="text-end">{formatCurrency(taxAmount)}</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td colSpan="3" className="text-end fw-bold">Total TTC</td>
                                            <td className="text-end fw-bold">{formatCurrency(totalTTC)}</td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>

                    {invoice.notes && (
                        <Card>
                            <Card.Header>
                                <h6 className="mb-0">Notes</h6>
                            </Card.Header>
                            <Card.Body>
                                <p className="mb-0">{invoice.notes}</p>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Section actions - seulement pour non-distributeurs */}
                    {!isDistributor && invoice.status !== 'PAID' && (
                        <div className="mt-4 text-end">
                            {invoice.status === 'PENDING' && (
                                <Button
                                    variant="success"
                                    onClick={handlePaymentRecording}
                                >
                                    <CreditCard size={16} className="me-2" />
                                    Enregistrer le paiement
                                </Button>
                            )}
                        </div>
                    )}
                </Card.Body>
                <Card.Footer className="text-muted">
                    <small>
                        Créée le: {formatDate(invoice.createdAt)}
                        {invoice.updatedAt && ` • Dernière modification: ${formatDate(invoice.updatedAt)}`}
                    </small>
                </Card.Footer>
            </Card>
        </div>
    );
}

export default InvoiceDetail;